/**
 * [API Docs] https://api.platform.cresclab.com/api/docs#tag/CustomField
 */
import { makeApi } from "@zodios/core";
import { z } from "zod";

import {
  CreateCustomFieldRequestSchema,
  CustomFieldSchema,
  PatchCustomFieldRequestSchema,
  UpdateCustomFieldRequestSchema,
} from "../models";

const api = makeApi([
  {
    alias: "listCustomFields",
    method: "get",
    path: "/api/v1/custom-fields/",
    parameters: [
      {
        type: "Query",
        name: "active",
        schema: CustomFieldSchema.shape.active.optional(),
      },
      {
        /**
         * Opt in to single select fields and their options. Interlude defaults
         * it to `false` so callers written before single select existed keep
         * seeing exactly the list they saw before; a caller that wants the new
         * type has to ask.
         *
         * Required rather than defaulted, and typed as the literal `true`,
         * which is the only value this app ever wants. Two things make that
         * worth the noise at the call sites. Zodios is configured with
         * `sendDefaults` at its default of `false`, so a `.default(true)`
         * would be recognized as the default and left out of the request --
         * the flag would read as set here and be absent on the wire. And a
         * call site added later cannot forget it: leaving it out is a
         * compile error rather than a list that silently drops every single
         * select field.
         */
        type: "Query",
        name: "enable_single_select",
        schema: z.literal(true),
      },
    ],
    response: z.array(CustomFieldSchema),
  },
  {
    alias: "createCustomField",
    method: "post",
    path: "/api/v1/custom-fields/",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: CreateCustomFieldRequestSchema,
      },
    ],
    response: CustomFieldSchema,
  },
  {
    alias: "updateCustomField",
    method: "put",
    path: "/api/v1/custom-fields/:fieldId",
    parameters: [
      {
        type: "Path",
        name: "fieldId",
        schema: CustomFieldSchema.shape.id,
      },
      {
        type: "Body",
        name: "body",
        schema: UpdateCustomFieldRequestSchema,
      },
    ],
    response: CustomFieldSchema,
  },
  {
    /**
     * Partial update, and the only way to change a single select field's
     * options. `updateCustomField` above is the older whole-field PUT, kept
     * for the field types that predate this endpoint and expected to be
     * retired.
     *
     * The response is left unparsed: no caller reads it. A mutation in this
     * api invalidates the api's queries when it settles, so the list refetches
     * itself and a schema here would only add a way for a successful write to
     * be reported as a failure.
     */
    alias: "patchCustomField",
    method: "patch",
    path: "/api/v1/custom-fields/:fieldId",
    parameters: [
      {
        type: "Path",
        name: "fieldId",
        schema: CustomFieldSchema.shape.id,
      },
      {
        type: "Body",
        name: "body",
        schema: PatchCustomFieldRequestSchema,
      },
    ],
    response: z.unknown(),
  },
  {
    alias: "archiveCustomField",
    method: "post",
    path: "/api/v1/custom-fields/:fieldId/archive",
    parameters: [
      {
        type: "Path",
        name: "fieldId",
        schema: CustomFieldSchema.shape.id,
      },
    ],
    response: CustomFieldSchema,
  },
  {
    alias: "restoreCustomField",
    method: "post",
    path: "/api/v1/custom-fields/:fieldId/restore",
    parameters: [
      {
        type: "Path",
        name: "fieldId",
        schema: CustomFieldSchema.shape.id,
      },
    ],
    response: CustomFieldSchema,
  },
]);

export { api };
